import { Fragment } from 'react';
import PropTypes from 'prop-types';
import _capitalize from 'lodash/capitalize';
import _groupBy from 'lodash/groupBy';
import _sortBy from 'lodash/sortBy';
import _map from 'lodash/map';
import Typography from '@material-ui/core/Typography';
import MailIcon from '@material-ui/icons/MailOutline';
import PhoneIcon from '@material-ui/icons/Phone';
import MLink from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import { momentParse } from '../../utils/time';
import MemberRoleToggle from './member-role-toggle';
import MemberRemoveButton from './member-remove-button';
import MaterialLink from '../material-link';
import Blockquote from '../blockquote';
import HostUser from '../host-user';

const useStyles = makeStyles((theme) => ({
  list: {
    padding: 0,
    margin: 0,
    listStyleType: 'none',
  },
  listItem: {
    margin: theme.spacing(2, 0),
  },
  memberName: {
    marginBottom: theme.spacing(1),
  },
  buttons: {
    margin: theme.spacing(1, 0),
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },
  memberEmail: {
    margin: theme.spacing(0.5, 0),
  },
  memberPhone: {
    margin: theme.spacing(0.5, 0),
  },
  emailLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#555',
    fontSize: 12,
    lineHeight: '15px',
  },
  emailIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 15,
  },
  joinMessage: {
    marginTop: theme.spacing(0.5),
    color: '#333',
    fontSize: 12,
  },
  requestMessage: {
    margin: theme.spacing(0, 0, 1, 0),
  },
}));

const GroupMemberList = ({ members, group }) => {
  const classes = useStyles();

  if (!members || !members.length) {
    return <p>No members</p>;
  }

  const membersByRole = _groupBy(
    _sortBy(members, (m) => ['REQUESTED', 'OWNER', 'MEMBER'].indexOf(m.role)),
    'role'
  );

  const roles = ['OWNER', 'MEMBER'];
  if (group.permissions.editable && membersByRole['REQUESTED']) {
    roles.unshift('REQUESTED');
  }

  return (
    <Fragment>
      {_map(roles, (role) => {
        const roleMembers = membersByRole[role] || [];
        const memberCount = roleMembers ? roleMembers.length : 0;
        const totalMemberCount = group.memberSummary[role] ?
          group.memberSummary[role].count :
          memberCount;
        const hiddenMemberCount = totalMemberCount - memberCount;
        return (
          <Fragment key={role}>
            <Typography variant="h3">{_capitalize(role)} ({totalMemberCount})</Typography>
            {totalMemberCount > 0 ? (
              <ul className={classes.list}>
                <Fragment>
                  {roleMembers.map((member) => {
                    const createdAt = momentParse(member.createdAt);
                    const approvedAt = member.approvedAt ? momentParse(member.approvedAt) : null;
                    let joinMessage;
                    if (approvedAt) {
                      joinMessage = (
                        <Fragment>
                          {' '}
                          via request to join
                          {member.approvedByUser && (
                            <Fragment>
                              {' – approved by '}
                              <MaterialLink href="/people/[username]" as={`/people/${member.approvedByUser.username}`}>
                                {member.approvedByUser.displayName}
                              </MaterialLink>
                            </Fragment>
                          )}
                        </Fragment>
                      );
                    } else if (member.invite) {
                      joinMessage = ` via invite ${member.invite.inviteType.toLowerCase()}`;
                    }
                    return (
                      <li key={member.id} className={classes.listItem}>
                        <HostUser user={member.user} className={classes.memberName} />
                        {group.permissions.editable && (
                          <Fragment>
                            {member.emailVisible && member.user.email && (
                              <div className={classes.memberEmail}>
                                <MLink href={`mailto:${member.user.email}`} className={classes.emailLink}>
                                  <MailIcon className={classes.emailIcon} />
                                  {member.user.email}
                                </MLink>
                              </div>
                            )}
                            {member.phoneNumberVisible && member.user.phoneNumberDisplay && (
                              <div className={classes.memberPhone}>
                                <MLink href={member.user.phoneNumberUri} className={classes.emailLink}>
                                  <PhoneIcon className={classes.emailIcon} />
                                  {member.user.phoneNumberDisplay}
                                </MLink>
                              </div>
                            )}
                            {member.role !== 'REQUESTED' && (
                              <div className={classes.joinMessage}>
                                <span>Joined {(approvedAt || createdAt).format('ll')}</span>
                                <span>{joinMessage}</span>
                              </div>
                            )}
                            {member.role === 'REQUESTED' && member.requestMessage && (
                              <Blockquote className={classes.requestMessage}>
                                {member.requestMessage}
                              </Blockquote>
                            )}
                            <div className={classes.buttons}>
                              <MemberRoleToggle member={member} />
                              <MemberRemoveButton member={member} group={group} />
                            </div>
                          </Fragment>
                        )}
                      </li>
                    );
                  })}
                </Fragment>
                {hiddenMemberCount > 0 && (
                  <li className={classes.hiddenItem}>{hiddenMemberCount} hidden</li>
                )}
              </ul>
            ) : (
              <p>None</p>
            )}
          </Fragment>
        )
      })}
    </Fragment>
  );
};

GroupMemberList.propTypes = {
  members: PropTypes.array,
  group: PropTypes.object,
};

export default GroupMemberList;
